'use client';

import React, {
  Fragment,
  useEffect,
  useMemo,
  useState,
} from 'react';

import Header from '../components/Header';
import Leftnav from '../components/Leftnav';
import Rightchat from '../components/Rightchat';
import Appfooter from '../components/Appfooter';
import Popupchat from '../components/Popupchat';
import VerifiedTick from '../components/VerifiedTick';

import {
  getLinkedMembersApi,
  getClubsApi,
} from '../api/ClientActions';

interface Member {
  contact_id: number;
  contact_name: string;
  contact_mobile?: string;
  contact_email?: string;
  profession?: string;
  state_name?: string;
  district_name?: string;
  taluk_name?: string;
  photo?: string;
}

interface Club {
  client_id: number;
  client_name: string;
  unique_slug?: string;
  logo_image?: string;
  verified_status?: number;
  total_members?: number;
  total_followers?: number;
  members?: Member[];
}

// ==========================
// API BASE FOR UPLOADS
// ==========================
const API_URL = (
  process.env.REACT_APP_API_URL ||
  'https://api.bsangam.com'
)
  .replace(/\/uploads\/?$/, '')
  .replace(/\/$/, '');

// ==========================
// UPLOAD URL HELPER
// ==========================
const getUploadUrl = (
  file?: string | null
) => {
  if (
    !file ||
    String(file).trim() === ''
  ) {
    return '/assets/images/group.svg';
  }

  const value =
    String(file).trim();

  // Already full URL
  if (
    value.startsWith('http://') ||
    value.startsWith('https://')
  ) {
    return encodeURI(value);
  }

  // /uploads/file.jpg
  if (
    value.startsWith('/uploads/')
  ) {
    return encodeURI(
      `${API_URL}${value}`
    );
  }

  // uploads/file.jpg
  if (
    value.startsWith('uploads/')
  ) {
    return encodeURI(
      `${API_URL}/${value}`
    );
  }

  // /other/path/file.jpg
  if (
    value.startsWith('/')
  ) {
    return encodeURI(
      `${API_URL}${value}`
    );
  }

  // filename.jpg
  return encodeURI(
    `${API_URL}/uploads/${value}`
  );
};

const MyClubsPage: React.FC = () => {
  const user = JSON.parse(
    localStorage.getItem('user') ||
      '{}'
  );

  const isLoggedIn =
    Boolean(
      user?.user_id
    );

  const [
    clubs,
    setClubs,
  ] = useState<Club[]>([]);

  const [
    loading,
    setLoading,
  ] = useState(false);

  const [
    searchTerm,
    setSearchTerm,
  ] = useState('');

  const [
    selectedClubId,
    setSelectedClubId,
  ] = useState('');

  // ==========================
  // FETCH CLUB DATA
  // Logged-in -> linked members
  // Guest -> public clubs
  // ==========================
  const fetchClubsData =
    async () => {
      try {
        setLoading(true);

        const result =
          isLoggedIn
            ? await getLinkedMembersApi({
                user_id:
                  user.user_id,
              })
            : await getClubsApi();

        if (
          result?.success === false ||
          result?.status === false
        ) {
          setClubs([]);
          return;
        }

        const responseData =
          Array.isArray(
            result?.data
          )
            ? result.data
            : Array.isArray(
                result?.clients
              )
            ? result.clients
            : [];

        setClubs(
          responseData
        );
      } catch (error) {
        console.error(
          'Clubs fetch error:',
          error
        );

        setClubs([]);
      } finally {
        setLoading(false);
      }
    };

  // ==========================
  // INITIAL LOAD
  // ==========================
  useEffect(() => {
    fetchClubsData();
  }, [
    user?.user_id,
  ]);

  // ==========================
  // FLATTEN ALL MEMBERS
  // ==========================
  const allMembers =
    useMemo(() => {
      return clubs.flatMap(
        (club) =>
          (
            club.members ||
            []
          ).map(
            (member) => ({
              ...member,

              client_id:
                club.client_id,

              client_name:
                club.client_name,

              logo_image:
                club.logo_image,

              verified_status:
                club.verified_status,
            })
          )
      );
    }, [clubs]);

  // ==========================
  // FILTER CLUBS
  // ==========================
  const filteredClubs =
    useMemo(() => {
      const search =
        searchTerm
          .toLowerCase()
          .trim();

      if (!search) {
        return clubs;
      }

      return clubs.filter(
        (club) =>
          club.client_name
            ?.toLowerCase()
            .includes(search)
      );
    }, [
      clubs,
      searchTerm,
    ]);

  // ==========================
  // FILTER MEMBERS
  // ==========================
  const filteredMembers =
    useMemo(() => {
      let members =
        allMembers;

      if (
        selectedClubId
      ) {
        members =
          members.filter(
            (
              member: any
            ) =>
              String(
                member.client_id
              ) ===
              String(
                selectedClubId
              )
          );
      }

      const search =
        searchTerm
          .toLowerCase()
          .trim();

      if (!search) {
        return members;
      }

      return members.filter(
        (
          member: any
        ) => {
          return (
            member.contact_name
              ?.toLowerCase()
              .includes(
                search
              ) ||
            member.contact_mobile
              ?.includes(
                search
              ) ||
            member.contact_email
              ?.toLowerCase()
              .includes(
                search
              ) ||
            member.profession
              ?.toLowerCase()
              .includes(
                search
              ) ||
            member.client_name
              ?.toLowerCase()
              .includes(
                search
              )
          );
        }
      );
    }, [
      allMembers,
      searchTerm,
      selectedClubId,
    ]);

  // ==========================
  // INITIALS
  // ==========================
  const getInitials = (
    name = ''
  ) => {
    return name
      .split(' ')
      .filter(Boolean)
      .map(
        (word) =>
          word[0]
      )
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // ==========================
  // CLEAR CLUB FILTER
  // ==========================
  const clearClubFilter =
    () => {
      setSelectedClubId(
        ''
      );
    };

  return (
    <Fragment>
      <Header />
      <Leftnav />
      <Rightchat />

      <div className="main-content right-chat-active clubs-main-page">
        <div className="middle-sidebar-bottom clubs-middle-bottom">
          <div className="middle-sidebar-left clubs-middle-left">
            <div className="clubs-page-wrapper">

              {/* ==========================
                  HEADER
              ========================== */}
              <div className="clubs-header-card">
                <div className="clubs-header-top">
                  <div>
                    <span className="clubs-page-badge">
                      Community Clubs
                    </span>

                    <h2 className="clubs-title">
                      {isLoggedIn ? 'Address Book' : 'Club Directory'}
                    </h2>

                    <p className="clubs-subtitle">
                      {isLoggedIn
                        ? 'Find and contact members from your joined clubs.'
                        : 'Explore Bharat Sangam clubs and communities.'}
                    </p>
                  </div>

                  <div className="clubs-header-stats">
                    <div>
                      <strong>
                        {
                          isLoggedIn
                            ? clubs.length
                            : filteredClubs.length
                        }
                      </strong>

                      <span>
                        Clubs
                      </span>
                    </div>

                    {isLoggedIn && (
                      <div>
                        <strong>
                          {
                            filteredMembers.length
                          }
                        </strong>

                        <span>
                          Members
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* ==========================
                    FILTERS
                ========================== */}
                <div
                  className={`clubs-filter-row ${
                    !isLoggedIn
                      ? 'clubs-filter-row-guest'
                      : ''
                  }`}
                >
                  <div className="clubs-search-box">
                    <i className="feather-search" />

                    <input
                      type="text"
                      placeholder={
                        isLoggedIn
                          ? 'Search name, mobile, email, profession...'
                          : 'Search clubs...'
                      }
                      value={
                        searchTerm
                      }
                      onChange={(
                        e
                      ) =>
                        setSearchTerm(
                          e.target.value
                        )
                      }
                    />
                  </div>

                  {isLoggedIn && (
                    <div className="clubs-select-wrap">
                      <select
                        value={
                          selectedClubId
                        }
                        onChange={(
                          e
                        ) =>
                          setSelectedClubId(
                            e.target.value
                          )
                        }
                      >
                        <option value="">
                          All Joined Clubs
                        </option>

                        {clubs.map(
                          (
                            club
                          ) => (
                            <option
                              key={
                                club.client_id
                              }
                              value={
                                club.client_id
                              }
                            >
                              {
                                club.client_name
                              }{' '}
                              (
                              {
                                club.total_members ||
                                  0
                              }
                              )
                            </option>
                          )
                        )}
                      </select>

                      <i className="feather-chevron-down" />
                    </div>
                  )}
                </div>
              </div>

              {/* ==========================
                  CLUBS
              ========================== */}
              {loading ? (
                <div className="clubs-section-block">
                  <div className="clubs-loader-card">
                    <div className="spinner-border text-primary" />

                    <p>
                      Loading clubs...
                    </p>
                  </div>
                </div>
              ) : isLoggedIn ? (
                clubs.length > 0 ? (
                  <div className="clubs-section-block">
                    <div className="clubs-section-header">
                      <div>
                        <h3>
                          Club Directory
                        </h3>

                        <p>
                          Choose a joined club to filter your address book.
                        </p>
                      </div>

                      {selectedClubId && (
                        <button
                          type="button"
                          className="clubs-clear-btn"
                          onClick={
                            clearClubFilter
                          }
                        >
                          Clear Filter
                        </button>
                      )}
                    </div>

                    <div className="clubs-grid">
                      {clubs.map(
                        (
                          club
                        ) => (
                          <div
                            className={`club-card ${
                              String(
                                selectedClubId
                              ) ===
                              String(
                                club.client_id
                              )
                                ? 'active-club-card'
                                : ''
                            }`}
                            key={
                              club.client_id
                            }
                            onClick={() =>
                              setSelectedClubId(
                                String(
                                  club.client_id
                                )
                              )
                            }
                          >
                            <div className="club-top">
                              <img
                                src={getUploadUrl(
                                  club.logo_image
                                )}
                                alt={
                                  club.client_name
                                }
                                className="club-logo"
                                onError={(
                                  e
                                ) => {
                                  e.currentTarget.src =
                                    '/assets/images/group.svg';
                                }}
                              />

                              <div className="club-main-info">
                                <h4 className="club-name">
                                  {
                                    club.client_name
                                  }

                                  {Number(
                                    club.verified_status
                                  ) ===
                                    1 && (
                                    <span className="club-verified">
                                      <VerifiedTick />
                                    </span>
                                  )}
                                </h4>
                              </div>
                            </div>

                            <div className="club-stats-row">
                              <div className="club-stat-box">
                                <h5>
                                  {
                                    club.total_members ||
                                      0
                                  }
                                </h5>

                                <span>
                                  Members
                                </span>
                              </div>

                              <div className="club-stat-box">
                                <h5>
                                  {
                                    club.total_followers ||
                                      0
                                  }
                                </h5>

                                <span>
                                  Followers
                                </span>
                              </div>
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="clubs-section-block">
                    <div className="clubs-empty-card">
                      <div className="clubs-empty-icon">
                        <i className="feather-users" />
                      </div>

                      <h4>
                        No joined clubs yet
                      </h4>

                      <p>
                        Clubs you join will appear here.
                      </p>
                    </div>
                  </div>
                )
              ) : (
                <div className="clubs-section-block">
                  <div className="clubs-section-header">
                    <div>
                      <h3>
                        Explore Clubs
                      </h3>

                      <p>
                        Discover clubs and communities on Bharat Sangam.
                      </p>
                    </div>
                  </div>

                  {filteredClubs.length > 0 ? (
                    <div className="clubs-grid">
                      {filteredClubs.map(
                        (
                          club
                        ) => (
                          <div
                            className="club-card guest-club-card"
                            key={
                              club.client_id
                            }
                          >
                            <div className="club-top">
                              <img
                                src={getUploadUrl(
                                  club.logo_image
                                )}
                                alt={
                                  club.client_name
                                }
                                className="club-logo"
                                onError={(
                                  e
                                ) => {
                                  e.currentTarget.src =
                                    '/assets/images/group.svg';
                                }}
                              />

                              <div className="club-main-info">
                                <h4 className="club-name">
                                  {
                                    club.client_name
                                  }

                                  {Number(
                                    club.verified_status
                                  ) ===
                                    1 && (
                                    <span className="club-verified">
                                      <VerifiedTick />
                                    </span>
                                  )}
                                </h4>
                              </div>
                            </div>

                            <div className="club-stats-row">
                              <div className="club-stat-box">
                                <h5>
                                  {
                                    club.total_members ||
                                      0
                                  }
                                </h5>

                                <span>
                                  Members
                                </span>
                              </div>

                              <div className="club-stat-box">
                                <h5>
                                  {
                                    club.total_followers ||
                                      0
                                  }
                                </h5>

                                <span>
                                  Followers
                                </span>
                              </div>
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  ) : (
                    <div className="clubs-empty-card">
                      <div className="clubs-empty-icon">
                        <i className="feather-search" />
                      </div>

                      <h4>
                        No clubs found
                      </h4>

                      <p>
                        Try another club name.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {isLoggedIn && (
                <>
              {/* ==========================
                  CLUB MEMBERS
              ========================== */}
              <div className="clubs-section-block">
                <div className="clubs-section-header">
                  <div>
                    <h3>
                      Member Contacts
                    </h3>

                    <p>
                      {selectedClubId
                        ? 'Contacts from the selected club.'
                        : 'All contacts from your joined clubs.'}
                    </p>
                  </div>
                </div>

                {/* LOADING */}
                {loading ? (
                  <div className="clubs-loader-card">
                    <div className="spinner-border text-primary" />

                    <p>
                      Loading
                      members...
                    </p>
                  </div>
                ) : filteredMembers.length >
                  0 ? (
                  <div className="members-grid">
                    {filteredMembers.map(
                      (
                        member: any
                      ) => (
                        <div
                          className="member-card address-book-card"
                          key={`${member.client_id}-${member.contact_id}`}
                        >
                          <div className="member-top">
                            <div className="member-avatar">
                              {member.photo ? (
                                <img
                                  src={getUploadUrl(member.photo)}
                                  alt={member.contact_name}
                                  onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                  }}
                                />
                              ) : (
                                getInitials(member.contact_name)
                              )}
                            </div>

                            <div className="member-main-info">
                              <h4>{member.contact_name || 'Member'}</h4>

                              <span className="member-club-pill">
                                <i className="feather-users" />
                                {member.client_name}
                              </span>
                            </div>
                          </div>

                          <div className="member-contact-list">
                            <div className="member-contact-row">
                              <span className="member-contact-icon">
                                <i className="feather-phone" />
                              </span>
                              <div>
                                <label>Mobile</label>
                                <strong>
                                  {member.contact_mobile || 'Not available'}
                                </strong>
                              </div>
                            </div>

                            <div className="member-contact-row">
                              <span className="member-contact-icon">
                                <i className="feather-mail" />
                              </span>
                              <div>
                                <label>Email</label>
                                <strong title={member.contact_email || ''}>
                                  {member.contact_email || 'Not available'}
                                </strong>
                              </div>
                            </div>

                            <div className="member-contact-row">
                              <span className="member-contact-icon">
                                <i className="feather-briefcase" />
                              </span>
                              <div>
                                <label>Profession</label>
                                <strong>
                                  {member.profession || 'Not available'}
                                </strong>
                              </div>
                            </div>

                            <div className="member-contact-row">
                              <span className="member-contact-icon">
                                <i className="feather-map-pin" />
                              </span>
                              <div>
                                <label>Location</label>
                                <strong
                                  title={[
                                    member.taluk_name,
                                    member.district_name,
                                    member.state_name,
                                  ]
                                    .filter(Boolean)
                                    .join(', ')}
                                >
                                  {[
                                    member.taluk_name,
                                    member.district_name,
                                    member.state_name,
                                  ]
                                    .filter(Boolean)
                                    .join(', ') || 'Not available'}
                                </strong>
                              </div>
                            </div>
                          </div>

                          <div className="member-quick-actions">
                            {member.contact_mobile ? (
                              <a
                                className="member-action-btn primary"
                                href={`tel:${member.contact_mobile}`}
                              >
                                <i className="feather-phone" />
                                Call
                              </a>
                            ) : (
                              <button
                                type="button"
                                className="member-action-btn disabled"
                                disabled
                              >
                                <i className="feather-phone" />
                                Call
                              </button>
                            )}

                            {member.contact_email ? (
                              <a
                                className="member-action-btn"
                                href={`mailto:${member.contact_email}`}
                              >
                                <i className="feather-mail" />
                                Email
                              </a>
                            ) : (
                              <button
                                type="button"
                                className="member-action-btn disabled"
                                disabled
                              >
                                <i className="feather-mail" />
                                Email
                              </button>
                            )}
                          </div>
                        </div>
                      )
                    )}
                  </div>
                ) : (
                  <div className="clubs-empty-card">
                    <div className="clubs-empty-icon">
                      <i className="feather-users" />
                    </div>

                    <h4>
                      No members found
                    </h4>

                    <p>
                      Try another search
                      or club filter.
                    </p>
                  </div>
                )}
              </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <Popupchat />
      <Appfooter />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:wght@600;700&family=Inter:wght@400;500;600;700&display=swap');

        .clubs-main-page {
          width: 100%;
          min-height: 100vh;
          padding-top: 58px;
          padding-left: 285px;
          padding-right: 315px;
          overflow-x: hidden;
          font-family: 'Inter', sans-serif;
          background: linear-gradient(180deg, #f8f9fc 0%, #f1f3f8 100%);
        }

        .clubs-middle-bottom,
        .clubs-middle-left {
          width: 100%;
          max-width: 100%;
        }

        .clubs-page-wrapper {
          width: 100%;
          max-width: 900px;
          margin-left: 22px;
          margin-top: 28px;
          padding-bottom: 40px;
        }

        /* ==========================
           HEADER — SAME DESIGN FAMILY
           AS TRENDING NEWS
        ========================== */

        .clubs-header-card {
          margin-bottom: 18px;
          padding: 14px 16px;
          border: 1px solid #e2eaf6;
          border-radius: 16px;
          background:
            linear-gradient(
              135deg,
              #ffffff 0%,
              #f7faff 58%,
              #f1f6ff 100%
            );
          box-shadow:
            0 5px 16px rgba(10, 35, 84, 0.045);
        }

        .clubs-header-top {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 10px;
        }

        .clubs-page-badge {
          display: none;
        }

        .clubs-title {
          margin: 0 0 4px;
          color: #0A2354;
          font-family: 'Fraunces', serif;
          font-size: 24px;
          font-weight: 700;
          letter-spacing: -0.01em;
          line-height: 1.2;
        }

        .clubs-subtitle {
          margin: 0;
          color: #8a93a3;
          font-size: 13px;
          line-height: 1.5;
          font-weight: 400;
        }

        .clubs-header-stats {
          display: flex;
          align-items: center;
          gap: 7px;
          flex-shrink: 0;
        }

        .clubs-header-stats div {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          min-height: 26px;
          padding: 0 9px;
          border: 1px solid #dce7f7;
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.78);
          white-space: nowrap;
        }

        .clubs-header-stats strong {
          color: #5f7fae;
          font-size: 11px;
          font-weight: 700;
          line-height: 1;
        }

        .clubs-header-stats span {
          margin: 0;
          color: #5f7fae;
          font-size: 11px;
          font-weight: 700;
          line-height: 1;
        }

        /* ==========================
           FILTERS
        ========================== */

        .clubs-filter-row {
          display: grid;
          grid-template-columns: minmax(0, 420px) 230px;
          gap: 10px;
          align-items: center;
        }

        .clubs-filter-row-guest {
          grid-template-columns: minmax(0, 420px);
        }

        .clubs-search-box {
          position: relative;
          width: 100%;
          height: 40px;
          display: flex;
          align-items: center;
        }

        .clubs-search-box i {
          position: absolute;
          left: 14px;
          top: 50%;
          z-index: 1;
          color: #9aa4b2;
          font-size: 14px;
          transform: translateY(-50%);
          pointer-events: none;
        }

        .clubs-search-box input {
          width: 100%;
          height: 40px;
          border: 1px solid #e5e8ef;
          border-radius: 10px;
          padding: 0 12px 0 36px;
          outline: none;
          color: #1f2937;
          background: rgba(255, 255, 255, 0.92);
          font-size: 13px;
          font-weight: 400;
          box-shadow:
            inset 0 1px 2px rgba(15, 23, 42, 0.05);
          transition:
            box-shadow 0.2s ease,
            border-color 0.2s ease;
        }

        .clubs-search-box input::placeholder {
          color: #9aa4b2;
        }

        .clubs-search-box input:focus {
          border-color: #2F6FE4;
          box-shadow:
            inset 0 1px 2px rgba(15, 23, 42, 0.04),
            0 0 0 3px rgba(47, 111, 228, 0.10);
        }

        .clubs-select-wrap {
          position: relative;
          height: 40px;
        }

        .clubs-select-wrap select {
          width: 100%;
          height: 40px;
          border: 1px solid #e5e8ef;
          border-radius: 10px;
          padding: 0 34px 0 12px;
          outline: none;
          appearance: none;
          color: #475569;
          background: rgba(255, 255, 255, 0.92);
          font-size: 12px;
          font-weight: 600;
          box-shadow:
            inset 0 1px 2px rgba(15, 23, 42, 0.04);
        }

        .clubs-select-wrap select:focus {
          border-color: #2F6FE4;
          box-shadow:
            0 0 0 3px rgba(47, 111, 228, 0.10);
        }

        .clubs-select-wrap i {
          position: absolute;
          right: 12px;
          top: 50%;
          color: #94a3b8;
          font-size: 14px;
          transform: translateY(-50%);
          pointer-events: none;
        }

        /* ==========================
           SECTIONS
        ========================== */

        .clubs-section-block {
          margin-bottom: 18px;
          padding: 16px;
          border: 1px solid #e9ecf2;
          border-radius: 16px;
          background: #ffffff;
          box-shadow:
            0 2px 8px rgba(15, 23, 42, 0.045);
        }

        .clubs-section-header {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 14px;
        }

        .clubs-section-header h3 {
          margin: 0;
          color: #0A2354;
          font-family: 'Fraunces', serif;
          font-size: 18px;
          font-weight: 700;
          line-height: 1.3;
        }

        .clubs-section-header p {
          margin: 4px 0 0;
          color: #8a93a3;
          font-size: 12px;
          line-height: 1.5;
        }

        .clubs-clear-btn {
          min-height: 30px;
          padding: 0 10px;
          border: 1px solid #dce7f7;
          border-radius: 999px;
          color: #2F6FE4;
          background: #f5f8ff;
          font-size: 11px;
          font-weight: 700;
          cursor: pointer;
          transition: background 0.2s ease;
        }

        .clubs-clear-btn:hover {
          background: #edf3ff;
        }

        /* ==========================
           GRIDS
        ========================== */

        .clubs-grid,
        .members-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 16px;
        }

        .members-grid {
          align-items: stretch;
        }

        /* ==========================
           CLUB CARD
        ========================== */

        .club-card,
        .member-card {
          min-width: 0;
          overflow: hidden;
          border: 1px solid #e9ecf2;
          border-radius: 16px;
          background: #ffffff;
          box-shadow:
            0 2px 8px rgba(15, 23, 42, 0.05);
          transition:
            transform 0.2s ease,
            border-color 0.2s ease,
            box-shadow 0.2s ease;
        }

        .club-card {
          padding: 14px;
          cursor: pointer;
        }

        .member-card {
          padding: 14px;
        }

        .guest-club-card {
          cursor: default;
        }

        .club-card:hover,
        .member-card:hover {
          transform: translateY(-2px);
          border-color: #c9d9f8;
          box-shadow:
            0 12px 28px rgba(10, 35, 84, 0.10);
        }

        .active-club-card {
          border-color: #9cbcf7;
          background:
            linear-gradient(
              145deg,
              #ffffff 0%,
              #f6f9ff 100%
            );
          box-shadow:
            0 8px 22px rgba(47, 111, 228, 0.10);
        }

        .club-top,
        .member-top {
          display: flex;
          align-items: center;
          gap: 10px;
          min-width: 0;
        }

        .club-logo {
          width: 46px;
          height: 46px;
          min-width: 46px;
          border: 1px solid #e4eaf4;
          border-radius: 13px;
          object-fit: cover;
          background: #f8fafc;
        }

        .club-main-info,
        .member-main-info {
          min-width: 0;
          flex: 1;
        }

        .club-name,
        .member-main-info h4 {
          display: flex;
          align-items: center;
          gap: 5px;
          min-width: 0;
          margin: 0;
          overflow: hidden;
          color: #0f1b34;
          font-family: 'Fraunces', serif;
          font-size: 14px;
          font-weight: 600;
          line-height: 1.35;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .club-verified {
          display: inline-flex;
          flex-shrink: 0;
          min-width: 15px;
        }

        .club-stats-row {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 8px;
          margin-top: 12px;
        }

        .club-stat-box {
          padding: 8px;
          border: 1px solid #edf1f6;
          border-radius: 10px;
          text-align: center;
          background: #f8fafc;
        }

        .club-stat-box h5 {
          margin: 0 0 2px;
          color: #2F6FE4;
          font-size: 15px;
          font-weight: 700;
          line-height: 1.2;
        }

        .club-stat-box span {
          color: #8a93a3;
          font-size: 10.5px;
          font-weight: 500;
        }

        /* ==========================
           MEMBER CARD
        ========================== */

        .member-avatar {
          width: 46px;
          height: 46px;
          min-width: 46px;
          overflow: hidden;
          border: 1px solid #dce7f7;
          border-radius: 13px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #2F6FE4;
          background: #eef4ff;
          font-size: 14px;
          font-weight: 700;
        }

        .member-avatar img {
          display: block;
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .member-main-info span {
          display: block;
          max-width: 100%;
          margin-top: 4px;
          overflow: hidden;
          color: #2F6FE4;
          font-size: 10.5px;
          font-weight: 600;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .member-info-list {
          display: flex;
          flex-direction: column;
          gap: 7px;
          margin-top: 12px;
        }

        .member-info-list div {
          min-width: 0;
          display: flex;
          align-items: center;
          gap: 7px;
          color: #64748b;
          font-size: 11.5px;
          line-height: 1.4;
        }

        .member-info-list i {
          width: 14px;
          min-width: 14px;
          color: #8fa8cf;
          font-size: 13px;
          text-align: center;
        }

        .member-info-list span {
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }


        /* ==========================
           ADDRESS BOOK
        ========================== */

        .address-book-card {
          padding: 14px;
          display: flex;
          flex-direction: column;
        }

        .address-book-card .member-top {
          padding-bottom: 12px;
          border-bottom: 1px solid #edf1f6;
        }

        .address-book-card .member-avatar {
          width: 52px;
          height: 52px;
          min-width: 52px;
          border-radius: 14px;
          font-size: 15px;
        }

        .address-book-card .member-main-info h4 {
          font-size: 15px;
          color: #0A2354;
        }

        .member-club-pill {
          width: fit-content;
          max-width: 100%;
          display: inline-flex !important;
          align-items: center;
          gap: 5px;
          margin-top: 5px !important;
          padding: 4px 7px;
          border: 1px solid #dce7f7;
          border-radius: 999px;
          color: #5f7fae !important;
          background: #f5f8ff;
          font-size: 9.5px !important;
          font-weight: 700 !important;
        }

        .member-club-pill i {
          font-size: 10px;
          flex-shrink: 0;
        }

        .member-contact-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-top: 12px;
        }

        .member-contact-row {
          min-width: 0;
          display: grid;
          grid-template-columns: 32px minmax(0, 1fr);
          align-items: center;
          gap: 8px;
          padding: 8px 9px;
          border: 1px solid #edf1f6;
          border-radius: 10px;
          background: #f8fafc;
        }

        .member-contact-icon {
          width: 32px;
          height: 32px;
          border-radius: 9px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #2F6FE4;
          background: #eef4ff;
          font-size: 13px;
        }

        .member-contact-row div {
          min-width: 0;
        }

        .member-contact-row label {
          display: block;
          margin: 0 0 2px;
          color: #94a3b8;
          font-size: 8.5px;
          font-weight: 700;
          line-height: 1.1;
          text-transform: uppercase;
          letter-spacing: 0.25px;
        }

        .member-contact-row strong {
          display: block;
          overflow: hidden;
          color: #475569;
          font-size: 10.5px;
          font-weight: 600;
          line-height: 1.3;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .member-quick-actions {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 8px;
          margin-top: 12px;
          padding-top: 12px;
          border-top: 1px solid #edf1f6;
        }

        .member-action-btn {
          min-height: 32px;
          padding: 0 10px;
          border: 1px solid #dce7f7;
          border-radius: 10px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          color: #2F6FE4;
          background: #f5f8ff;
          font-size: 10.5px;
          font-weight: 700;
          text-decoration: none;
          cursor: pointer;
          transition: 0.2s ease;
        }

        .member-action-btn:hover {
          background: #edf3ff;
          color: #1f57c7;
          text-decoration: none;
        }

        .member-action-btn.primary {
          border-color: #2F6FE4;
          color: #ffffff;
          background: #2F6FE4;
        }

        .member-action-btn.primary:hover {
          color: #ffffff;
          background: #1f57c7;
        }

        .member-action-btn.disabled {
          border-color: #e5e7eb;
          color: #a8b0bd;
          background: #f8fafc;
          cursor: not-allowed;
        }

        /* ==========================
           LOADING / EMPTY
        ========================== */

        .clubs-loader-card,
        .clubs-empty-card {
          width: 100%;
          min-height: 190px;
          padding: 28px 22px;
          border: 1px dashed #dbe2ec;
          border-radius: 16px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          background: #ffffff;
        }

        .clubs-loader-card p {
          margin: 10px 0 0;
          color: #94a3b8;
          font-size: 11.5px;
        }

        .clubs-empty-icon {
          width: 44px;
          height: 44px;
          margin-bottom: 11px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #5f7fae;
          background: #eef4fb;
        }

        .clubs-empty-icon i {
          font-size: 20px;
        }

        .clubs-empty-card h4 {
          margin: 0;
          color: #475569;
          font-size: 14px;
          font-weight: 700;
        }

        .clubs-empty-card p {
          max-width: 330px;
          margin: 6px 0 0;
          color: #94a3b8;
          font-size: 11.5px;
          line-height: 1.55;
        }

        /* ==========================
           RESPONSIVE
        ========================== */

        @media (max-width: 1400px) {
          .clubs-main-page {
            padding-right: 290px;
          }
        }

        @media (max-width: 1200px) {
          .clubs-main-page {
            padding-left: 20px;
            padding-right: 20px;
          }

          .clubs-page-wrapper {
            margin-left: 0;
            max-width: 100%;
          }

          .clubs-grid,
          .members-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
        }

        @media (max-width: 900px) {
          .clubs-grid,
          .members-grid {
            grid-template-columns: 1fr;
          }
        }

        @media (max-width: 768px) {
          .clubs-main-page {
            padding-top: 16px;
            padding-left: 12px;
            padding-right: 12px;
          }

          .clubs-middle-bottom,
          .clubs-middle-left {
            padding-left: 0 !important;
            padding-right: 0 !important;
            margin-left: 0 !important;
            margin-right: 0 !important;
          }

          .clubs-page-wrapper {
            margin-top: 0;
            margin-left: 0;
            max-width: 100%;
            padding-bottom: 76px;
          }

          .clubs-header-card {
            padding: 12px 13px;
            border-radius: 14px;
          }

          .clubs-title {
            font-size: 21px;
          }

          .clubs-header-top {
            gap: 10px;
          }

          .clubs-header-stats {
            gap: 5px;
          }

          .clubs-header-stats div {
            min-height: 24px;
            padding: 0 7px;
          }

          .clubs-filter-row {
            grid-template-columns: 1fr;
          }

          .clubs-section-block {
            padding: 13px;
            border-radius: 14px;
          }

          .clubs-section-header {
            align-items: flex-start;
          }
        }

        @media (max-width: 480px) {
          .clubs-header-top {
            flex-direction: column;
          }

          .clubs-header-stats {
            width: 100%;
          }

          .clubs-header-stats div {
            justify-content: center;
            flex: 1;
          }

          .clubs-section-header {
            flex-direction: column;
          }
        }
      `}</style>
    </Fragment>
  );
};

export default MyClubsPage;